/* custom (Second Edition): Arriving Soon collection behavior.
   1. The "How pre-order works" links open the pre-order FAQ row below the grid.
   2. On the /collections/all?view=arriving-soon preview route, native filter, sort and page
      requests keep the view, like the Fall Kickoff sale view.
   3. Preview only: the pre-order pieces are Shopify drafts, so an unpublished preview theme renders
      them from a snapshot in the live card markup. The snapshot never renders on a published theme. */
(() => {
  'use strict';
  if (window.seArrivingSoonInitialized) return;
  window.seArrivingSoonInitialized = true;

  const VIEW = 'arriving-soon';
  const FAQ_HASH = '#pre-order-faq';
  const PREVIEW_THEME_ID = 188553003311;

  function openFaq(scroll) {
    const anchor = document.getElementById('pre-order-faq');
    if (!anchor) return false;
    const section = anchor.closest('.shopify-section');
    let next = section ? section.nextElementSibling : null;
    let row = null;
    while (next && !row) {
      row = next.querySelector('collapsible-row');
      next = next.nextElementSibling;
    }
    if (row) {
      const details = row.querySelector('details');
      if (details && !details.open) {
        if (typeof row.open === 'function') row.open();
        else details.open = true;
      }
    }
    if (scroll) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }

  function initFaqLinks() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('a[href="' + FAQ_HASH + '"]');
      if (!link) return;
      if (!openFaq(true)) return;
      event.preventDefault();
      if (window.location.hash !== FAQ_HASH) history.replaceState(history.state, '', FAQ_HASH);
    });
    if (window.location.hash === FAQ_HASH) openFaq(false);
  }

  function initViewPreservation(hero) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') !== VIEW) return;
    const grid = document.getElementById('ProductGridContainer');
    const catalog = grid && grid.closest('.shopify-section');

    function preserveLink(link) {
      if (!link) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (url.searchParams.get('view') === VIEW) return;
      url.searchParams.set('view', VIEW);
      link.href = url.pathname + url.search + url.hash;
    }

    function refresh() {
      const current = new URLSearchParams(window.location.search);
      if (hero && hero.dataset.hideAfterFirst === 'true') hero.hidden = Number(current.get('page') || 1) > 1;
      if (!catalog) return;
      catalog.querySelectorAll('facet-filters-form form').forEach((form) => {
        let input = form.querySelector('input[name="view"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'view';
          form.appendChild(input);
        }
        if (input.value !== VIEW) input.value = VIEW;
      });
      catalog.querySelectorAll('facet-remove a, pagination-theme a, .collection-empty a').forEach(preserveLink);
    }

    refresh();
    if (catalog) {
      new MutationObserver(refresh).observe(catalog, { childList: true, subtree: true });
      catalog.addEventListener('click', (event) => {
        preserveLink(event.target.closest('facet-remove a, pagination-theme a, .collection-empty a'));
      }, true);
    }
    window.addEventListener('popstate', refresh);
  }

  const escape = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  // Matches the store money format ${{amount_no_decimals}}.
  const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 });

  function imageUrl(value, width) {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com' || url.username || url.password) return '';
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (_) { return ''; }
  }

  function normalize(item) {
    if (!item || typeof item !== 'object') return null;
    const title = typeof item.title === 'string' ? item.title.trim() : '';
    const price = Number(item.price);
    if (!title || !/^[a-z0-9][a-z0-9-]*$/.test(String(item.handle || '')) || !Number.isFinite(price) || price <= 0) return null;
    const compareAt = Number(item.compareAt);
    return { handle: item.handle, title, price, compareAt: Number.isFinite(compareAt) && compareAt > price ? compareAt : 0, image: typeof item.image === 'string' ? item.image : '' };
  }

  // Same DOM as snippets/product-card.liquid with the live settings (square ratio, left aligned,
  // no border, no hover images, price after the title, enlarged prices). Draft product URLs 404,
  // so every link goes to the pre-order FAQ, and the compare box is left out.
  function card(item, labels) {
    const title = escape(item.title);
    const small = imageUrl(item.image, 375);
    const large = imageUrl(item.image, 770);
    const image = small
      ? '<img class=" product-primary-image" width="1200" height="1200" sizes="(min-width: 1068px) 25vw, 50vw" src="' + escape(small) + '" srcset="' + escape(small) + ' 375w,' + escape(large) + ' 770w" loading="lazy" alt="' + title + '">'
      : '<div class="thb-placeholder"></div>';
    return '<li class="columns" data-se-preorder-snapshot-item="' + escape(item.handle) + '">'
      + '<product-card class="product-card text-left product-card--border-false product-card--preorder">'
      + '<figure class="product-card-image ">'
      + '<span class="product-card--badges ' + escape(labels.position) + '"><span class="badge pre-order badge--se-preorder ' + escape(labels.position) + '">' + escape(labels.preOrder) + '</span></span>'
      + '<a title="' + title + '" class="product-card-image-link aspect-ratio aspect-ratio--square" style="--padding-bottom: 100%;">' + image + '</a>'
      + '</figure>'
      + '<div class="product-card-info">'
      + '<div class="product-card-info--inner"><a title="' + title + '" class="product-card-title">' + title + '</a>'
      + '<span class="price large-price">'
      // Same markup as the live card's compare-at price: <del> retail, then <ins> price.
      + (item.compareAt ? '<del><span class="amount discounted">' + escape(currency.format(item.compareAt)) + '</span></del> ' : '')
      + '<ins><span class="amount">' + escape(currency.format(item.price)) + '</span></ins>'
      + '<small class="unit-price  hidden"><span></span><span class="unit-price-separator">/</span><span></span></small></span>'
      + '</div>'
      + '<div class="product-card-info--bottom product-card-info--bottom-disabled"><a class="button outline small product-card--view-details">' + escape(labels.viewDetails) + '</a></div>'
      + '</div>'
      + '</product-card></li>';
  }

  function initSnapshot(hero) {
    const source = hero && hero.dataset.seSnapshot;
    if (!source) return;
    const theme = window.Shopify && window.Shopify.theme;
    // Preview boundary: publishing this theme must never expose the draft snapshot.
    if (!theme || Number(theme.id) !== PREVIEW_THEME_ID || !['unpublished', 'development'].includes(theme.role)) return;
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    const labels = {
      position: hero.dataset.badgePosition || 'top left',
      preOrder: hero.dataset.preOrderLabel || 'Pre-order',
      viewDetails: hero.dataset.viewAs || 'View details'
    };
    let markup = '';
    let rendering = false;

    function render() {
      if (!markup || rendering) return;
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;
      const products = container.querySelector('.sidebar-container--products');
      if (!products) return;
      let list = products.querySelector('#product-grid.products');
      if (list && list.dataset.seSnapshot === 'rendered') return;
      rendering = true;
      try {
        const empty = products.querySelector('.collection-empty');
        if (!list) {
          list = document.createElement('ul');
          list.id = 'product-grid';
          list.className = 'products collection row small-up-2 medium-up-4 pagination--paginated';
          (empty || products.firstChild) ? products.insertBefore(list, empty || products.firstChild) : products.appendChild(list);
        }
        if (empty) empty.style.display = 'none';
        list.querySelectorAll(':scope > li:not(.loading-overlay)').forEach((item) => item.remove());
        list.insertAdjacentHTML('afterbegin', markup);
        list.dataset.seSnapshot = 'rendered';
        products.querySelectorAll('pagination-theme, .pagination--wrapper').forEach((node) => { node.style.display = 'none'; });
        document.documentElement.classList.add('se-preorder-snapshot');
      } finally {
        rendering = false;
      }
    }

    fetch(source, { credentials: 'same-origin' })
      .then((response) => { if (!response.ok) throw new Error('snapshot ' + response.status); return response.json(); })
      .then((data) => {
        if (!data || Number(data.draftThemeOnly) !== PREVIEW_THEME_ID || !Array.isArray(data.items)) return;
        const items = data.items.map(normalize).filter(Boolean);
        if (!items.length) return;
        markup = items.map((item) => card(item, labels)).join('');
        render();
        // Native filter and sort responses replace the grid; render the snapshot again.
        new MutationObserver(render).observe(grid.closest('.shopify-section') || grid, { childList: true, subtree: true });
      })
      .catch(() => { /* The native grid stays in place when the snapshot cannot load. */ });
  }

  function initialize() {
    const hero = document.querySelector('[data-se-arriving-hero]');
    initFaqLinks();
    initViewPreservation(hero);
    initSnapshot(hero);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
